import { apiRequest, withRetry, api } from "./api";
import type {
  SupportChat,
  SupportChatDetail,
  Message,
  SendMessageForm,
  CreateChatForm,
  UpdateChatStatusForm,
  PaginatedResponse,
  ChatListParams,
  MessageListParams,
  ReassignmentTarget,
  User,
  InternalChat,
  InternalChatDetail,
  InternalMessage,
  SendInternalMessageForm,
  CreateInternalChatForm,
  InternalChatListParams,
  InternalMessageListParams,
  InternalChatUserListParams,
  InternalChatUser,
} from "@/types";

// Chat API functions
export const pingAPI = {
  // Chat endpoints
  chats: {
    // GET /api/pings/chats/ - List chats
    list: async (
      params?: ChatListParams
    ): Promise<PaginatedResponse<SupportChat>> => {
      const queryParams = new URLSearchParams();
      if (params?.patient_name)
        queryParams.append("patient_name", params.patient_name);
      if (params?.status) queryParams.append("status", params.status);
      if (params?.is_starred !== undefined)
        queryParams.append("is_starred", params.is_starred.toString());
      if (params?.has_unread !== undefined)
        queryParams.append("has_unread", params.has_unread.toString());
      if (params?.is_unread !== undefined)
        queryParams.append("is_unread", params.is_unread.toString());
      if (params?.assignee) queryParams.append("assignee", params.assignee);
      if (params?.unassigned !== undefined)
        queryParams.append("unassigned", params.unassigned.toString());
      if (params?.assigned !== undefined)
        queryParams.append("assigned", params.assigned.toString());
      if (params?.assigned_to_me !== undefined)
        queryParams.append("assigned_to_me", params.assigned_to_me.toString());
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.page_size)
        queryParams.append("page_size", params.page_size.toString());

      return withRetry(() =>
        apiRequest(() => api.get(`/pings/chats/?${queryParams.toString()}`))
      );
    },

    // POST /api/pings/chats/ - Create chat
    create: async (data: CreateChatForm): Promise<SupportChat> => {
      return withRetry(() => apiRequest(() => api.post("/pings/chats/", data)));
    },

    // GET /api/pings/chats/{id}/ - Get chat detail
    get: async (chatId: string): Promise<SupportChatDetail> => {
      return withRetry(() =>
        apiRequest(() => api.get(`/pings/chats/${chatId}/`))
      );
    },

    // PATCH /api/pings/chats/{id}/ - Reassign chat to care manager (matches Flutter)
    reassign: async (
      chatId: string,
      careManagerId: string
    ): Promise<SupportChat> => {
      return withRetry(() =>
        apiRequest(() =>
          api.patch(`/pings/chats/${chatId}/`, { care_manager: careManagerId })
        )
      );
    },

    // GET /api/pings/chats/{id}/reassignment-targets/ - Get eligible reassignment targets
    getReassignmentTargets: async (
      chatId: string
    ): Promise<ReassignmentTarget[]> => {
      return withRetry(() =>
        apiRequest(() =>
          api.get(`/pings/chats/${chatId}/reassignment-targets/`)
        )
      );
    },

    // PUT /api/pings/chats/{id}/status/ - Update chat status
    updateStatus: async (
      chatId: string,
      data: UpdateChatStatusForm
    ): Promise<SupportChat> => {
      return withRetry(() =>
        apiRequest(() => api.put(`/pings/chats/${chatId}/status/`, data))
      );
    },

    // PATCH /pings/chats/{id}/starred/ - Mark chat as starred
    star: async (chatId: string): Promise<SupportChat> => {
      return withRetry(() =>
        apiRequest(() => api.patch(`/pings/chats/${chatId}/starred/`))
      );
    },

    // PATCH /pings/chats/{id}/unread/ - Mark chat as unread
    markAsUnread: async (chatId: string): Promise<SupportChat> => {
      return withRetry(() =>
        apiRequest(() => api.patch(`/pings/chats/${chatId}/unread/`))
      );
    },
  },

  // User/Console endpoints
  users: {
    // GET /api/users/list/ - Search users/patients by name (matches Flutter implementation)
    searchPatients: async (name: string = ""): Promise<User[]> => {
      return withRetry(() =>
        apiRequest(() =>
          api.get(`/users/list/?name=${encodeURIComponent(name)}`)
        )
      );
    },
  },

  // Message endpoints
  messages: {
    // GET /api/pings/messages/ - List messages for a chat
    list: async (
      params: MessageListParams
    ): Promise<PaginatedResponse<Message>> => {
      const queryParams = new URLSearchParams();
      queryParams.append("chat_id", params.chat_id);
      if (params.consolidated !== undefined) {
        queryParams.append("consolidated", params.consolidated);
      }
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.page_size)
        queryParams.append("page_size", params.page_size.toString());

      return withRetry(() =>
        apiRequest(() => api.get(`/pings/messages/?${queryParams.toString()}`))
      );
    },

    // POST /api/pings/messages/ - Create message
    create: async (data: SendMessageForm): Promise<Message> => {
      // Handle multipart form data for media uploads
      const formData = new FormData();
      formData.append("chat", data.chat);
      formData.append("message_type", data.message_type);

      if (data.text_content) {
        formData.append("text_content", data.text_content);
      }

      if (data.media_file) {
        formData.append("media_file", data.media_file);
      }

      if (data.is_note !== undefined) {
        formData.append("is_note", data.is_note.toString());
      }

      return withRetry(() =>
        apiRequest(() =>
          api.post("/pings/messages/", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
        )
      );
    },

    // PUT /api/pings/messages/{id}/read/ - Mark messages as read
    markAsRead: async (messageId: string): Promise<{ count: number }> => {
      return withRetry(() =>
        apiRequest(() => api.put(`/pings/messages/${messageId}/read/`))
      );
    },
  },

  // Internal Chat endpoints
  internalChats: {
    // GET /api/ping/internal-chats/users/ - List available users to chat with
    listAvailableUsers: async (
      params?: InternalChatUserListParams
    ): Promise<PaginatedResponse<InternalChatUser>> => {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append("search", params.search);
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.page_size)
        queryParams.append("page_size", params.page_size.toString());

      return withRetry(() =>
        apiRequest(() =>
          api.get(`/pings/internal-chats/users/?${queryParams.toString()}`)
        )
      );
    },

    // POST /api/ping/internal-chats/ - Create or get chat
    create: async (data: CreateInternalChatForm): Promise<InternalChat> => {
      return withRetry(() =>
        apiRequest(() => api.post("/pings/internal-chats/", data))
      );
    },

    // GET /api/ping/internal-chats/ - List user's chats
    list: async (
      params?: InternalChatListParams
    ): Promise<PaginatedResponse<InternalChat>> => {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append("search", params.search);
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.page_size)
        queryParams.append("page_size", params.page_size.toString());

      return withRetry(() =>
        apiRequest(() =>
          api.get(`/pings/internal-chats/?${queryParams.toString()}`)
        )
      );
    },

    // GET /api/ping/internal-chats/{id}/ - Get chat detail
    get: async (chatId: string): Promise<InternalChatDetail> => {
      return withRetry(() =>
        apiRequest(() => api.get(`/pings/internal-chats/${chatId}/`))
      );
    },

    // PATCH /api/ping/internal-chats/{id}/starred/ - Toggle starred status
    star: async (chatId: string): Promise<InternalChat> => {
      return withRetry(() =>
        apiRequest(() =>
          api.patch(`/pings/internal-chats/${chatId}/starred/`, {})
        )
      );
    },

    // PATCH /api/ping/internal-chats/{id}/unread/ - Toggle unread status
    markAsUnread: async (chatId: string): Promise<InternalChat> => {
      return withRetry(() =>
        apiRequest(() =>
          api.patch(`/pings/internal-chats/${chatId}/unread/`, {})
        )
      );
    },
  },

  // Internal Message endpoints
  internalMessages: {
    // GET /api/ping/internal-messages/ - List messages for a chat
    list: async (
      params: InternalMessageListParams
    ): Promise<PaginatedResponse<InternalMessage>> => {
      const queryParams = new URLSearchParams();
      queryParams.append("chat_id", params.chat_id);
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.page_size)
        queryParams.append("page_size", params.page_size.toString());

      return withRetry(() =>
        apiRequest(() =>
          api.get(`/pings/internal-messages/?${queryParams.toString()}`)
        )
      );
    },

    // POST /api/ping/internal-messages/ - Create message
    create: async (data: SendInternalMessageForm): Promise<InternalMessage> => {
      // Handle multipart form data for media uploads
      const formData = new FormData();
      formData.append("chat", data.chat);
      formData.append("message_type", data.message_type);

      if (data.text_content) {
        formData.append("text_content", data.text_content);
      }

      if (data.media_file) {
        formData.append("media_file", data.media_file);
      }

      if (data.media_thumbnail) {
        formData.append("media_thumbnail", data.media_thumbnail);
      }

      return withRetry(() =>
        apiRequest(() =>
          api.post("/pings/internal-messages/", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
        )
      );
    },

    // PATCH /api/ping/internal-messages/{id}/read/ - Mark messages as read
    markAsRead: async (
      messageId: string
    ): Promise<{ status: string; marked_count: number; message: string }> => {
      return withRetry(() =>
        apiRequest(() =>
          api.patch(`/pings/internal-messages/${messageId}/read/`, {})
        )
      );
    },
  },
};

// Helper functions for common operations
export const pingHelpers = {
  // Create a text message
  sendTextMessage: async (
    chatId: string,
    text: string,
    isNote: boolean = false
  ): Promise<Message> => {
    return await pingAPI.messages.create({
      chat: chatId,
      message_type: "text",
      text_content: text,
      is_note: isNote,
    });
  },

  // Create an internal text message
  sendInternalTextMessage: async (
    chatId: string,
    text: string
  ): Promise<InternalMessage> => {
    return await pingAPI.internalMessages.create({
      chat: chatId,
      message_type: "text",
      text_content: text,
    });
  },
};

export default pingAPI;
